import React from "react";
import PropTypes from "prop-types";
import getStyled from "../../hooks/getStyled";

import SFSkeleton from "../skeleton";
import { SFGridContainer, SFGridItem, SFStack } from "../grid";
import SFHaloIconWrapper from "../icons/halo";
import { SFInfoIcon } from "../icons";
import SFTypography from "../typography";
import SFTooltip from "../tooltip";
import { Logger } from "@base-provider";

const { styled } = getStyled();

const StyledContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.5),
  border: `1px solid ${theme.palette.grey225.main}`,
  backgroundColor: theme.palette.grey100.main,
  borderRadius: "4px",
  overflow: "hidden",
  height: "100%",
}));

const SFHighlightCard = ({
  title,
  count,
  variant,
  ignoreIcon,
  icon,
  details = [],
  loading = true,
  titleVariant = "body1bold",
  tooltip = null,
  titleComponents,
  description,
  className,
}) => {
  Logger.of("App.core.Card.Highlight").info({
    title,
    count,
    details,
    variant,
    loading,
  });

  if (loading) {
    return <SFSkeleton />;
  }

  return (
    <StyledContainer className={className}>
      <SFGridContainer>
        <SFGridItem xs={12}>
          <SFStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <SFStack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <SFHaloIconWrapper
                variant={variant}
                ignoreIcon={ignoreIcon}
                size="medium"
              >
                {icon}
              </SFHaloIconWrapper>
              <SFTypography variant={titleVariant}>{title}</SFTypography>
              {tooltip && (
                <SFTooltip width="narrow" content={tooltip}>
                  <div>
                    <SFInfoIcon
                      color="grey400"
                      variant="unhovered"
                      size="small"
                    />
                  </div>
                </SFTooltip>
              )}
            </SFStack>
            {!!count && typeof count === "string" ? (
              <SFTypography variant="cardNumberLarge" hasHtml>
                {count}
              </SFTypography>
            ) : (
              count
            )}
            {titleComponents}
          </SFStack>
        </SFGridItem>
        {description && (
          <SFGridItem xs={12}>
            <SFTypography variant="body2">{description}</SFTypography>
          </SFGridItem>
        )}
        <SFGridItem xs={12}>
          <SFStack direction="column" justifyContent="space-between">
            {details.map((detail, index) => (
              <SFTypography variant="body1" key={index}>
                <SFTypography variant="body1bold" component="span">
                  {`${detail.count} `}
                </SFTypography>
                {detail.label}
              </SFTypography>
            ))}
          </SFStack>
        </SFGridItem>
      </SFGridContainer>
    </StyledContainer>
  );
};

SFHighlightCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  titleComponents: PropTypes.node,
  description: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  details: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      label: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
};

export default SFHighlightCard;
